import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { registerServiceWorker } from './app/serviceWorker';
import { isThemePreference, resolveTheme, THEME_COLOR } from './core/theme';
import { loadEngine } from './engine/load';
import { readPreference } from './io/preferences';
import { useEditorStore } from './state/editorStore';
import './index.css';

/**
 * Paint the right theme before React mounts.
 *
 * The store reads the preference synchronously too, but React renders a frame
 * later; without this a light-theme user would see the dark shell flash first.
 */
function applyInitialTheme(): void {
  const stored = readPreference('theme');
  const preference = isThemePreference(stored) ? stored : 'system';
  // Ask about dark, not light: a browser reporting 'no-preference' must land on
  // light, which is what every browser means by it.
  const resolved = resolveTheme(
    preference,
    window.matchMedia('(prefers-color-scheme: dark)').matches,
  );
  document.documentElement.dataset.theme = resolved;
  document.documentElement.style.colorScheme = resolved;
  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
    meta.setAttribute('content', THEME_COLOR[resolved] ?? THEME_COLOR.dark);
  }
}

applyInitialTheme();

// The renderer is its own chunk (see `engine/load.ts`). Start fetching it now so
// it lands while React is still doing its first render, rather than when the
// stage mounts a moment later.
void loadEngine();

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

// Dev serves unbundled modules that change constantly; caching them would make
// development feel broken. The worker is a production-only concern.
if (import.meta.env.PROD) {
  void registerServiceWorker((state) => {
    useEditorStore.getState().setUpdateAvailable(state === 'ready');
  });
}

window.addEventListener('online', () => useEditorStore.getState().setOnline(true));
window.addEventListener('offline', () => useEditorStore.getState().setOnline(false));
